import { compact } from 'lodash';
import { format, parseISO } from 'date-fns';

import { PARTNER_STATUS } from '../constants';

export const dashIfEmpty = (value) =>
  value === null || value === undefined || value === '' ? '—' : value;

// Catalog-side label: name plus strength. `drugName` may already carry the
// strength depending on how the catalog composed it, so avoid repeating it.
export const catalogName = ({ drugName, strength } = {}) => {
  const name = drugName ?? '';
  if (!name) return '—';
  if (!strength) return name;
  return name.toLowerCase().includes(String(strength).toLowerCase())
    ? name
    : `${name} ${strength}`.trim();
};

// The form to show. formAlias is the label the catalog carries when it has one,
// with dosageForm as the fallback when a row has no alias yet -- showing a dash
// there would read as missing data when the form is actually known.
export const formLabel = ({ dosageForm, formAlias } = {}) => formAlias || dosageForm || '';

// Pack size needs both halves to mean anything: "30" with no unit, or "ct" with no
// count, is worse than saying nothing.
export const packSizeLabel = ({ packSizeValue, packSizeUnit } = {}) =>
  packSizeValue && packSizeUnit ? `${packSizeValue} ${packSizeUnit}` : '';

// Secondary caption under the catalog name: form and pack size, the same shape the
// catalog and inventory screens use, so the three read alike. Form comes from
// formLabel rather than listing dosageForm AND formAlias — they are usually the
// same word, and printing both gave captions like "Tablet · Tablet".
export const buildPackaging = (item = {}) =>
  compact([formLabel(item), packSizeLabel(item)]).join(' · ');

// numeric:true so "5 mg" sorts before "10 mg"; base sensitivity so casing is ignored.
export const compareAlphabetically = (readText) => (a, b) =>
  String(readText(a) ?? '').localeCompare(String(readText(b) ?? ''), undefined, {
    sensitivity: 'base',
    numeric: true,
  });

// Timestamps come back as ISO strings. A malformed or absent value renders as a
// dash rather than "Invalid Date".
export const formatTimestamp = (value) => {
  if (!value) return '—';
  try {
    return format(parseISO(value), 'MMM d, yyyy');
  } catch {
    return '—';
  }
};

// Derived from the partner record in the store, not from the preference query.
// Returns null when the partner is not in the store yet, so the row shows nothing
// rather than asserting "Active" on missing data.
export const partnerStatusOf = (partner) => {
  if (!partner) return null;
  if (partner.isSuspended) return PARTNER_STATUS.SUSPENDED;
  if (partner.enabled === false) return PARTNER_STATUS.INACTIVE;
  return PARTNER_STATUS.ACTIVE;
};

// Every column the child table shows is searchable, so what is on screen is what
// the box matches against — nothing hidden, nothing missing.
//
// Matching is token-based across one joined haystack rather than substring-per-
// field, which buys two things people actually type. A stray space still finds
// the drug ("am lo" -> Amlodipine), and terms can span columns ("teva lisinopril"
// matches the manufacturer and the name together), which a per-field substring
// check can never do. NDCs also get a separator-stripped copy in the haystack,
// since they are pasted around in whichever format the source used.
export const preferenceMatches = (preference, term) => {
  const tokens = String(term ?? '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return true;

  const haystack = [
    catalogName(preference),
    buildPackaging(preference),
    preference.productId,
    preference.ndc,
    String(preference.ndc ?? '').replace(/\D/g, ''),
    preference.manufacturer,
    preference.inventoryDrugName,
  ]
    .map((field) => String(field ?? '').toLowerCase())
    .join(' ');

  return tokens.every((token) => haystack.includes(token));
};

// Sort comparator for the child table. Timestamps compare as ISO strings, which
// is chronological; everything else is a case-insensitive locale compare.
export const comparePreferences = (key, order) => (a, b) => {
  const pick = (row) => {
    if (key === 'catalogName') return catalogName(row);
    return row[key] ?? '';
  };
  const left = String(pick(a));
  const right = String(pick(b));
  const result = left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
  return order === 'asc' ? result : -result;
};
